import React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import Header from "../../components/header/header";
import Footer from "../../components/footer/footer";
import Animate from "../about/Animate";
import { ItemNotFound } from "../404/404";
import { getProviders } from "../../services/providers.service";
import { HOME_ROUTERS_PLANS } from "../../../config";
import { HomeRouterPlan } from "../../../types";
import Rs from "../../components/design/rs";
import {
  routerToItem,
  trackFilterApplied,
  trackSelectItem,
  trackViewItemList,
} from "../../tools/tracking";
import "./homeRouters.css";

const PER_PAGE = 9;
const ROUTERS_LIST_ID = "home_routers";

// pick the EN/AR field of a plan based on the active language
const localized = (plan: HomeRouterPlan, key: string, lang: string) =>
  (plan as any)[key + (lang === "ar" ? "Ar" : "En")];

const planNetwork = (plan: HomeRouterPlan) =>
  plan.network || (/5G/i.test(plan.routerTypeEn) ? "5G" : "4G");

const HomeRouters: React.FC = () => {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;
  const allPlans = HOME_ROUTERS_PLANS as HomeRouterPlan[];

  const [search, setSearch] = React.useState("");
  const [orderBy, setOrderBy] = React.useState("price_asc");
  const [network, setNetwork] = React.useState("all");
  const [commitment, setCommitment] = React.useState("all");
  const [unlimitedOnly, setUnlimitedOnly] = React.useState(false);
  const [currentPage, setCurrentPage] = React.useState(1);

  // providerId -> provider, so cards can show provider name/logo
  const [providers, setProviders] = React.useState<Record<number, any>>({});

  React.useEffect(() => {
    let active = true;
    getProviders(lang)
      .then((res) => {
        if (!active) return;
        const map: Record<number, any> = {};
        (res?.returnData || []).forEach((p: any) => {
          map[p.id] = p;
        });
        setProviders(map);
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [lang]);

  // distinct commitment periods available, for the dropdown
  const commitmentOptions = React.useMemo(
    () =>
      Array.from(
        new Set(allPlans.map((p) => p.commitmentPeriodMonths))
      ).sort((a, b) => a - b),
    [allPlans]
  );

  const filtered = React.useMemo(() => {
    let list = allPlans.filter((plan) => {
      const name = (
        localized(plan, "name", lang) +
        " " +
        plan.nameEn +
        " " +
        plan.nameAr
      ).toLowerCase();
      if (search && !name.includes(search.toLowerCase())) return false;
      if (network !== "all" && planNetwork(plan) !== network) return false;
      if (
        commitment !== "all" &&
        plan.commitmentPeriodMonths !== Number(commitment)
      )
        return false;
      if (unlimitedOnly && !/unlimited/i.test(plan.dataEn)) return false;
      return true;
    });

    list = [...list].sort((a, b) => {
      switch (orderBy) {
        case "price_desc":
          return b.discountedPrice - a.discountedPrice;
        case "name_asc":
          return localized(a, "name", lang).localeCompare(
            localized(b, "name", lang)
          );
        case "name_desc":
          return localized(b, "name", lang).localeCompare(
            localized(a, "name", lang)
          );
        case "price_asc":
        default:
          return a.discountedPrice - b.discountedPrice;
      }
    });

    return list;
  }, [allPlans, lang, search, network, commitment, unlimitedOnly, orderBy]);

  // any time the filters change, jump back to the first page
  React.useEffect(() => {
    setCurrentPage(1);
  }, [search, network, commitment, unlimitedOnly, orderBy]);

  const filtersTouched = React.useRef(false);
  React.useEffect(() => {
    // skip the initial render — the default filter state is not a user action
    if (!filtersTouched.current) {
      filtersTouched.current = true;
      return;
    }
    trackFilterApplied({
      listId: ROUTERS_LIST_ID,
      filters: { search, network, commitment, unlimitedOnly, orderBy },
      resultCount: filtered.length,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, network, commitment, unlimitedOnly, orderBy]);

  React.useEffect(() => {
    trackViewItemList({
      items: pageItems.map((plan) =>
        routerToItem(
          plan,
          allPlans.indexOf(plan),
          lang,
          providers[plan.providerId]
        )
      ),
      listId: ROUTERS_LIST_ID,
      listName: "Home Routers",
    });
    // `pageItems` is a fresh slice on every render, so the memoised `filtered`
    // and the page number are the stable stand-ins
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtered, currentPage, lang]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageItems = filtered.slice(
    (currentPage - 1) * PER_PAGE,
    currentPage * PER_PAGE
  );

  // search/filter controls are hidden for now
  const showFilters = false;

  return (
    <>
      <div className="home-routers__section">
        <div className="container">
          <Header />

          <div className="home-routers__hero text-center my-5">
            <h1 className="fw-bold">{t("home_routers.title")}</h1>
            <p className="text-muted">{t("home_routers.subtitle")}</p>
          </div>

          {/* filters */}
          {showFilters && (
          <div className="home-routers__filters">
            <div className="hr-search">
              <i className="fas fa-search" />
              <input
                type="text"
                placeholder={t("home_routers.search_placeholder") as string}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            <div className="hr-controls">
              <select
                className="hr-select"
                value={network}
                onChange={(e) => setNetwork(e.target.value)}
              >
                <option value="all">{t("home_routers.all_networks")}</option>
                <option value="5G">5G</option>
                <option value="4G">4G</option>
              </select>

              <select
                className="hr-select"
                value={commitment}
                onChange={(e) => setCommitment(e.target.value)}
              >
                <option value="all">{t("home_routers.all_commitments")}</option>
                {commitmentOptions.map((c) => (
                  <option value={c} key={c}>
                    {t("home_routers.commitment", { count: c })}
                  </option>
                ))}
              </select>

              <select
                className="hr-select"
                value={orderBy}
                onChange={(e) => setOrderBy(e.target.value)}
              >
                <option value="price_asc">{t("home_routers.sort_price_asc")}</option>
                <option value="price_desc">{t("home_routers.sort_price_desc")}</option>
                <option value="name_asc">{t("home_routers.sort_name_asc")}</option>
                <option value="name_desc">{t("home_routers.sort_name_desc")}</option>
              </select>

              <label className="hr-checkbox">
                <input
                  type="checkbox"
                  checked={unlimitedOnly}
                  onChange={() => setUnlimitedOnly((v) => !v)}
                />
                {t("home_routers.unlimited_only")}
              </label>
            </div>
          </div>
          )}

          {/* results */}
          {pageItems.length === 0 ? (
            <ItemNotFound
              title={t("home_routers.no_plans_found")}
              description={t("home_routers.no_plans_description")}
              button={t("home_routers.clear_filters")}
              onClick={() => {
                setSearch("");
                setNetwork("all");
                setCommitment("all");
                setUnlimitedOnly(false);
                setOrderBy("price_asc");
              }}
            />
          ) : (
            <div className="row pb-3">
              {pageItems.map((plan, index) => (
                <div className="col-12 col-md-6 col-xl-4 mb-4" key={index}>
                  <Animate delay={index * 0.05}>
                    <Link
                      to={`/home-routers/${allPlans.indexOf(plan)}`}
                      className="router-card__link"
                      onClick={() =>
                        trackSelectItem(
                          routerToItem(
                            plan,
                            allPlans.indexOf(plan),
                            lang,
                            providers[plan.providerId]
                          ),
                          ROUTERS_LIST_ID
                        )
                      }
                    >
                      <RouterPlanCard
                        plan={plan}
                        lang={lang}
                        t={t}
                        provider={providers[plan.providerId]}
                      />
                    </Link>
                  </Animate>
                </div>
              ))}
            </div>
          )}

          {/* pagination */}
          {totalPages > 1 && (
            <ul className="pagination justify-content-center pb-5">
              {Array(totalPages)
                .fill(0)
                .map((_, index) => {
                  const page = index + 1;
                  if (page < currentPage + 3 && page > currentPage - 3) {
                    return (
                      <li className="page-item" key={index}>
                        <span
                          className={`page-link ${
                            page === currentPage ? "active" : ""
                          }`}
                          style={{ cursor: "pointer" }}
                          onClick={() => setCurrentPage(page)}
                        >
                          {page}
                        </span>
                      </li>
                    );
                  } else if (
                    page === currentPage - 3 ||
                    page === currentPage + 3
                  ) {
                    return (
                      <li className="page-item" key={index}>
                        <span className="page-link">...</span>
                      </li>
                    );
                  }
                  return null;
                })}
            </ul>
          )}
        </div>
      </div>
      <Footer />
    </>
  );
};

const RouterPlanCard = ({
  plan,
  lang,
  t,
  provider,
}: {
  plan: HomeRouterPlan;
  lang: string;
  t: any;
  provider?: any;
}) => {
  const packageDetails: string[] =
    localized(plan, "packageDetails", lang) || [];
  const terms: string[] = localized(plan, "terms", lang) || [];

  return (
    <div className="router-card h-100 d-flex flex-column">
      {plan.imageUrl ? (
        <img
          className="router-card__image"
          src={plan.imageUrl}
          alt={localized(plan, "name", lang)}
        />
      ) : null}

      <div className="d-flex justify-content-between align-items-start">
        <span className="router-card__marketing">
          {localized(plan, "marketingMessage", lang)}
        </span>
        <span className="router-card__network">{planNetwork(plan)}</span>
      </div>

      {provider ? (
        <div className="router-card__provider">
          {provider.imagePathSmall ? (
            <img
              className="router-card__provider-logo"
              src={provider.imagePathSmall}
              alt={provider.name}
            />
          ) : null}
          <span className="router-card__provider-name">{provider.name}</span>
        </div>
      ) : null}

      {plan.show === false ? (
        <div className="router-card__name-row">
          <h2 className="router-card__name mb-0">
            {localized(plan, "name", lang)}
          </h2>
          <div className="router-card__price">
            <span className="router-card__price-value">
              {plan.regularPrice}
            </span>
            <span className="router-card__price-currency">
              <Rs size={18} />
            </span>
          </div>
        </div>
      ) : (
        <>
          <h2 className="router-card__name">{localized(plan, "name", lang)}</h2>
          <div className="router-card__price">
            <span className="router-card__price-value">
              {plan.discountedPrice}
            </span>
            <span className="router-card__price-currency">
              <Rs size={18} />
            </span>
            <span className="router-card__price-note">
              {t("home_routers.for_first_months", {
                count: plan.discountDurationMonths,
              })}
            </span>
          </div>
          <p className="router-card__regular">
            {t("home_routers.then_regular", {
              price: plan.regularPrice,
            })}{" "}
            <Rs size={14} color="#6b6880" />
          </p>
        </>
      )}

      <ul className="router-card__specs">
        <li>
          <span>{t("home_routers.data")}</span>
          <strong>{localized(plan, "data", lang)}</strong>
        </li>
        <li>
          <span>{t("home_routers.download_speed")}</span>
          <strong>{localized(plan, "downloadSpeed", lang)}</strong>
        </li>
        <li>
          <span>{t("home_routers.upload_speed")}</span>
          <strong>{localized(plan, "uploadSpeed", lang)}</strong>
        </li>
        <li>
          <span>{t("home_routers.router")}</span>
          <strong>{localized(plan, "routerType", lang)}</strong>
        </li>
        <li>
          <span>{t("home_routers.router_price")}</span>
          <strong>{localized(plan, "routerPrice", lang)}</strong>
        </li>
      </ul>

      <div className="router-card__block">
        <h3>{t("home_routers.package_details")}</h3>
        <ul className="router-card__list">
          {packageDetails.map((detail, i) => (
            <li key={i}>{detail}</li>
          ))}
        </ul>
      </div>

      <div className="router-card__block">
        <h3>{t("home_routers.terms")}</h3>
        <ul className="router-card__list router-card__list--muted">
          {terms.map((term, i) => (
            <li key={i}>{term}</li>
          ))}
        </ul>
      </div>

      <button className="router-card__cta mt-auto">
        {t("home_routers.subscribe")}
      </button>
    </div>
  );
};

export default HomeRouters;
